import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, finalize, map, of, Subject, switchMap, takeUntil } from 'rxjs';
import { App } from '../../models/app.model';
import { AppSearchService } from '../../services/app-search.service';

@Component({
    selector: 'app-selector',
    templateUrl: 'app-selector.component.html',
    styleUrls: ['app-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AppSelectorComponent implements OnInit, OnDestroy {
    @Input() placeholderText: string;
    @Input() selectedApp: App;
    @Input() parentFormGroup: FormGroup;
    @Input() controlName: string;

    @Output() selectedAppChange: EventEmitter<App> = new EventEmitter<App>();

    appSelectorControl: FormControl<App> = new FormControl<App>(null);
    appFilterControl: FormControl<string> = new FormControl<string>('');
    filteredApps: Subject<App[]> = new Subject<App[]>();
    readonly searching = signal(false);
    readonly compareApps = (first: App | null, second: App | null): boolean => first?.id === second?.id;

    private readonly destroy$ = new Subject<void>();
    private searchResults: App[] = [];

    constructor(private readonly appSearchService: AppSearchService) { }

    ngOnInit(): void {
        this.appFilterControl.valueChanges
            .pipe(
                map(search => search?.trim() ?? ''),
                debounceTime(500),
                distinctUntilChanged(),
                switchMap(search => {
                    if (!search) {
                        this.searching.set(false);
                        return EMPTY;
                    }

                    this.searching.set(true);
                    return this.appSearchService.getApps(search).pipe(
                        catchError(() => of([])),
                        finalize(() => this.searching.set(false))
                    );
                }),
                takeUntil(this.destroy$)
            ).subscribe(result => {
                this.searchResults = this.uniqueApps(result);
                this.emitAvailableApps();
            });

        this.appSelectorControl.valueChanges.pipe(
            takeUntil(this.destroy$)
        ).subscribe(app => {
            this.selectedAppChange.emit(app);
            this.emitAvailableApps();
        });
    }

    clearSelection(): void {
        this.appSelectorControl.setValue(null);
    }

    private emitAvailableApps(): void {
        const selected = this.appSelectorControl.value;
        const selectedIsMissing = selected && !this.searchResults.some(app => app.id === selected.id);
        this.filteredApps.next(selectedIsMissing ? [selected, ...this.searchResults] : this.searchResults);
    }

    private uniqueApps(apps: App[]): App[] {
        const ids = new Set<string>();
        return apps.filter(app => {
            if (ids.has(app.id)) {
                return false;
            }

            ids.add(app.id);
            return true;
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
